import { Text } from '@foss/legion-ui'
import propsData from '../generated/props.json'
import styles from './PropsTable.module.css'

export interface PropEntry {
  name: string
  type: { label: string; values: string[] }
  required: boolean
  default: string | null
  description: string
}

export interface PropsEntry {
  component: string
  interface: string
  description: string
  props: PropEntry[]
}

const DATA = propsData as unknown as Record<string, PropsEntry>

/** The root entry for a component, plus any compound parts, in order. */
export function entriesFor(component: string): [string, PropsEntry][] {
  const root = DATA[component]
  const parts = Object.entries(DATA)
    .filter(([key]) => key.startsWith(`${component}.`))
    .sort(([a], [b]) => a.localeCompare(b))
  return root ? [[component, root], ...parts] : parts
}

function TypeCell({ type }: { type: PropEntry['type'] }) {
  if (!type.values.length) {
    return <code className={styles.type}>{type.label}</code>
  }
  return (
    <span className={styles.union}>
      {type.values.map((value) => (
        <code className={styles.value} key={value}>
          {value}
        </code>
      ))}
    </span>
  )
}

export function PropsTable({ component }: { component: string }) {
  const entries = entriesFor(component)

  if (!entries.length) {
    return (
      <Text tone="muted" data-testid="props-none">
        This component takes no props of its own beyond the native attributes it
        spreads onto its root element.
      </Text>
    )
  }

  return (
    <>
      {entries.map(([key, entry]) => (
        <section key={key} data-testid={`props-section-${key}`}>
          <Text variant="subheading" as="h3" className={styles.heading}>
            <code>{key}</code>
          </Text>
          {entry.description && <Text tone="muted">{entry.description}</Text>}
          <div className={styles.wrap}>
            <table className={styles.table} data-testid={`props-table-${key}`}>
              <thead>
                <tr>
                  <th>Prop</th>
                  <th>Type</th>
                  <th>Default</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {entry.props.map((prop) => (
                  <tr key={prop.name} data-testid={`prop-${key}-${prop.name}`}>
                    <td>
                      <code className={styles.name}>{prop.name}</code>
                      {prop.required && (
                        <span className={styles.required}>required</span>
                      )}
                    </td>
                    <td>
                      <TypeCell type={prop.type} />
                    </td>
                    <td>
                      {prop.default ? (
                        <code className={styles.default}>{prop.default}</code>
                      ) : (
                        <span className={styles.none}>—</span>
                      )}
                    </td>
                    <td>{prop.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
      <Text tone="muted" variant="body-sm" data-testid="props-native-note">
        Native attributes — <code>id</code>, <code>className</code>,{' '}
        <code>data-*</code>, <code>aria-*</code>, event handlers — spread onto
        the root element and are not listed here.
      </Text>
    </>
  )
}
